import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StartupApplicationForm } from "./StartupApplicationForm";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface StartupRole {
  id: string;
  title: string;
  description: string;
  requirements?: string | null;
}

interface StartupRolesProps {
  roles?: StartupRole[];
  startupId: string;
  isOwner?: boolean;
  onRoleDelete?: (roleId: string) => void;
  onRoleEdit?: (role: StartupRole) => void;
}

export const StartupRoles = ({ roles, startupId, isOwner, onRoleDelete, onRoleEdit }: StartupRolesProps) => {
  const { toast } = useToast();
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (roleId: string) => {
    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('startup_roles')
        .delete()
        .eq('id', roleId);

      if (error) {
        console.error('Error deleting role:', error);
        toast({
          title: "Error",
          description: "Failed to delete role",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Role deleted successfully",
      });
      
      onRoleDelete?.(roleId);
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setRoleToDelete(null);
    }
  };

  if (!roles?.length) return null;

  return (
    <div>
      <h2 className="text-2xl font-bold text-flux-cyan mb-4 flex items-center gap-2">
        <Briefcase className="w-6 h-6" />
        Open Positions
      </h2>
      <div className="grid gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="bg-flux-black/50 border-flux-cyan/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-flux-cyan">{role.title}</CardTitle>
                {isOwner && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onRoleEdit?.(role)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog open={roleToDelete === role.id} onOpenChange={(open) => !open && setRoleToDelete(null)}>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => setRoleToDelete(role.id)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete this role
                            and remove all associated applications.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-500 hover:bg-red-600"
                            onClick={() => handleDelete(role.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "Deleting..." : "Delete Role"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              <CardDescription>{role.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {role.requirements && (
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Requirements:</h4>
                  <p className="text-gray-400">{role.requirements}</p>
                </div>
              )}
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-white text-corporate-black hover:bg-corporate-gray-100">
                    Apply Now
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Apply for {role.title}</DialogTitle>
                  </DialogHeader>
                  <StartupApplicationForm
                    startupId={startupId}
                    roleId={role.id}
                    onSuccess={() => {
                      toast({
                        title: "Success",
                        description: "Your application has been submitted.",
                      });
                    }}
                  />
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};